import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Search, Plus, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Clock, List, LayoutList, Building2, DollarSign, Package } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, buildAchatsImportExportProps, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, resolveAchatsEntityReference, AchatsConfigNotice, isAchatsCatalogLowStock, fetchAchatsProducts, fetchAchatsCatalog, fetchAchatsSuppliers, findAchatsCatalogItem, countAchatsLowStockCatalog, buildAchatsProductSelectOptions, mapAchatsProductSelection, ensureAchatsDatePickerLocales, useAchatsListViewMode, useAchatsListDisplay, achatsSettingsFromConfig } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import { getDefaultApprovalStatus } from './achatsConfigDefaults';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

ensureAchatsDatePickerLocales();

const DemandesAchatPage = () => {
  const { t, i18n } = useTranslation();
  const ns = 'modules.achats.pages.demandesAchatPage';
  const p = (key, opts) => {
    const val = t(`${ns}.${key}`, opts);
    return val && val !== `${ns}.${key}` ? val : t(`${ns}.${key}`, { ...opts, lng: 'ar' });
  };
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const dateLocale = getBrandDatePickerLocale();
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(getBrandLocale(i18n.language)) : '-';
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'converted', label: p('status_converted'), icon: Package, style: 'bg-green-500/20 text-green-400 border-green-500/30' }
  ];

  const urgencyOptions = [
    { value: 'low', label: p('urgency_low') },
    { value: 'normal', label: p('urgency_normal') },
    { value: 'high', label: p('urgency_high') },
    { value: 'urgent', label: p('urgency_urgent') }
  ];

  const mainTabs = [
    { id: 'all_requests', name: p('tab_all_requests'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: AlertCircle },
    { id: 'par_urgence', name: p('tab_by_urgency'), icon: FileText }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'pending', name: p('quickFilter_pending'), icon: Clock },
    { id: 'approved', name: p('quickFilter_approved'), icon: CheckCircle },
    { id: 'converted', name: p('quickFilter_converted'), icon: Package },
  ];

  const getUrgencyLabel = (value) => urgencyOptions.find((o) => o.value === value)?.label || value || '-';

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      const isClickOnReasonButton = event.target.closest('[data-reason-dropdown-button]');
      const isClickInReasonDropdown = event.target.closest('[data-reason-dropdown]');
      if (!isClickOnReasonButton && !isClickInReasonDropdown) {
        setShowReasonDropdown(false);
      }
      
      const isClickOnRefundButton = event.target.closest('[data-refund-dropdown-button]');
      const isClickInRefundDropdown = event.target.closest('[data-refund-dropdown]');
      if (!isClickOnRefundButton && !isClickInRefundDropdown) {
        setShowRefundDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_return_info'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'details', label: p('section_refund_notes'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({
    request_number: '',
    requester_name: '',
    requester_department: '',
    product_id: '',
    product_name: '',
    product_code: '',
    quantity: '',
    unit_price: '',
    total_amount: '',
    urgency: 'normal',
    status: 'pending',
    request_date: new Date().toISOString().split('T')[0],
    required_date: '',
    supplier_suggestion: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [showRefundDropdown, setShowRefundDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_requests');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Dropdown data states
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [availablePurchaseOrders, setAvailablePurchaseOrders] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const reasonDropdownRef = useRef(null);
  const refundDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadRequests(); 
    loadSuppliers();
    loadPurchaseOrders();
    loadProducts();
  }, []);

  useEffect(() => {
    if (!achatsConfig.lowStockAlertEnabled) {
      setCatalogItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const items = await fetchAchatsCatalog();
      if (!cancelled) setCatalogItems(items);
    })();
    return () => { cancelled = true; };
  }, [achatsConfig.lowStockAlertEnabled]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRequests = async () => {
    try {
      const response = await localApiAdapter.get('/achats/demandes');
      setRequests(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading purchase requests:', error);
      setRequests([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await localApiAdapter.get('/achats/purchase-orders');
      setAvailablePurchaseOrders(response?.data || response || []);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      setAvailablePurchaseOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const products = await fetchAchatsProducts();
      setAvailableProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const productOptions = useMemo(
    () => buildAchatsProductSelectOptions(availableProducts, catalogItems, {
      lowStockAlertEnabled: achatsConfig.lowStockAlertEnabled,
    }),
    [availableProducts, catalogItems, achatsConfig.lowStockAlertEnabled],
  );

  const lowStockCatalogCount = useMemo(
    () => (achatsConfig.lowStockAlertEnabled ? countAchatsLowStockCatalog(catalogItems) : 0),
    [achatsConfig.lowStockAlertEnabled, catalogItems],
  );

  const getRequestCatalogItem = useCallback(
    (record) => findAchatsCatalogItem(catalogItems, {
      productId: record?.product_id,
      productCode: record?.product_code,
      productName: record?.product_name,
    }),
    [catalogItems],
  );

  const handleProductSelection = (productId) => {
    if (!productId) {
      setFormData((prev) => ({ ...prev, product_id: '', product_name: '', product_code: '' }));
      return;
    }
    const option = productOptions.find((opt) => String(opt.value) === String(productId));
    const product = option?.product || availableProducts.find((item) => String(item.id) === String(productId));
    if (product) {
      const patch = mapAchatsProductSelection(product, catalogItems, {
        lowStockAlertEnabled: achatsConfig.lowStockAlertEnabled,
      });
      setFormData((prev) => {
        const qty = parseFloat(prev.quantity) || 0;
        const unitPrice = parseFloat(patch.unit_price) || 0;
        return {
          ...prev,
          ...patch,
          total_amount: qty && unitPrice ? (qty * unitPrice).toFixed(2) : prev.total_amount,
        };
      });
    }
  };

  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData(prev => ({ ...prev, supplier_id: '', supplier_name: '' }));
      return;
    }
    const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
    if (supplier) {
      setFormData(prev => ({ 
        ...prev, 
        supplier_id: supplierId,
        supplier_name: supplier.company_name || supplier.name || ''
      }));
    }
  };

  const handleOrderSelection = (orderId) => {
    if (!orderId) {
      setFormData(prev => ({ ...prev, original_order_id: '', original_order_number: '' }));
      return;
    }
    const order = availablePurchaseOrders.find(o => o.id.toString() === orderId.toString());
    if (order) {
      setFormData(prev => ({ 
        ...prev, 
        original_order_id: orderId,
        original_order_number: order.order_number || '',
        supplier_id: order.supplier_id || '',
        supplier_name: order.supplier_name || '',
        total_amount: order.total_amount || ''
      }));
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    converted: requests.filter(r => r.status === 'converted').length,
    totalAmount: requests.reduce((sum, r) => sum + (parseFloat(r.total_amount) || 0), 0),
  };


  const returnDistribution = {
    all_requests: {
      title: p('dist_allReturnsTitle'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'converted', name: p('dist_converted'), count: stats.converted, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_statut: {
      title: p('dist_byStatusTitle'),
      data: [
        { filterKey: 'in_progress', name: p('dist_inProgress'), count: stats.pending + stats.approved, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'finished', name: p('dist_finished'), count: stats.converted, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_urgence: {
      title: p('dist_byReasonTitle'),
      data: (() => {
        const urgencyCounts = {
          high: requests.filter(r => r.urgency === 'high').length,
          urgent: requests.filter(r => r.urgency === 'urgent').length,
          normal: requests.filter(r => r.urgency === 'normal').length,
        };
        return [
          { filterKey: 'high', name: p('dist_high'), count: urgencyCounts.high, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
          { filterKey: 'urgent', name: p('dist_urgent'), count: urgencyCounts.urgent, color: "bg-gradient-to-r from-red-500 to-red-600" },
          { filterKey: 'normal', name: p('dist_normal'), count: urgencyCounts.normal, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        ];
      })()
    }
  };

  const distributionInfo = returnDistribution[mainViewMode] || returnDistribution.all_requests;

  const baseFilteredRequests = requests.filter(r => {
    if (dateRange.start || dateRange.end) {
      const returnDate = new Date(r.request_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (returnDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (returnDate > endOfDay) return false;
      }
    }
    
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchTerm && !r.request_number?.toLowerCase().includes(searchTerm.toLowerCase())
      && !r.requester_name?.toLowerCase().includes(searchTerm.toLowerCase())
      && !r.product_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_requests') {
      if (quickFilter === 'pending' && r.status !== 'pending') return false;
      if (quickFilter === 'approved' && r.status !== 'approved') return false;
      if (quickFilter === 'converted' && r.status !== 'converted') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_requests':
          if (activeDistributionFilter === 'pending' && r.status !== 'pending') return false;
          if (activeDistributionFilter === 'approved' && r.status !== 'approved') return false;
          if (activeDistributionFilter === 'converted' && r.status !== 'converted') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'in_progress' && !['pending', 'approved'].includes(r.status)) return false;
          if (activeDistributionFilter === 'finished' && r.status !== 'converted') return false;
          break;
        case 'par_urgence':
          if (r.urgency !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('request_number'),
      textColumnRule('requester_name'),
      textColumnRule('request_date', (r) => (r.request_date ? fmtDate(r.request_date) : '')),
      textColumnRule('product_name'),
      textColumnRule('total_amount', (r) => String(r.total_amount ?? '')),
      exactColumnRule('urgency', (r) => r.urgency || ''),
      exactColumnRule('status', (r) => r.status || ''),
      textColumnRule('required_date', (r) => (r.required_date ? fmtDate(r.required_date) : '')),
    ],
    [i18n.language],
  );

  const { filtered: filteredRequests, displayed: displayedRequests } = useAchatsListDisplay(
    baseFilteredRequests,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedRecords, filteredRequests);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({
      columnKey,
      label,
      colFilters,
      setColFilter,
      editingKey,
      setEditingKey,
      ...opts,
    });

  const generateRequestNumber = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'purchaseRequest', 'DREQ', requests, (r) => r.request_number),
    [achatsSettings, requests],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const qty = parseFloat(formData.quantity) || 0;
      const unitPrice = parseFloat(formData.unit_price) || 0;
      const requestNumber = resolveAchatsEntityReference(
        achatsSettings,
        'purchaseRequest',
        'DREQ',
        requests,
        (r) => r.request_number,
        formData.request_number,
      );
      const submitData = {
        request_number: requestNumber,
        requester_name: formData.requester_name,
        requester_department: formData.requester_department,
        product_id: formData.product_id ? parseInt(formData.product_id, 10) || null : null,
        product_name: formData.product_name,
        product_code: formData.product_code || null,
        quantity: qty,
        unit_price: unitPrice,
        total_amount: parseFloat(formData.total_amount) || qty * unitPrice,
        urgency: formData.urgency || 'normal',
        status: formData.status || getDefaultApprovalStatus(achatsSettings, 'purchaseRequest'),
        request_date: formData.request_date || new Date().toISOString().split('T')[0],
        required_date: formData.required_date || null,
        supplier_suggestion: formData.supplier_suggestion || null,
        notes: formData.notes || null,
      };

      if (editingRecord) {
        await localApiAdapter.put(`/achats/demandes-achat/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/demandes', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
    } finally {
      resetForm();
      loadRequests();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      request_number: record.request_number || '',
      requester_name: record.requester_name || '',
      requester_department: record.requester_department || '',
      product_id: record.product_id?.toString() || '',
      product_name: record.product_name || '',
      product_code: record.product_code || '',
      quantity: record.quantity?.toString() || '',
      unit_price: record.unit_price?.toString() || '',
      total_amount: record.total_amount?.toString() || '',
      urgency: record.urgency || 'normal',
      status: record.status || 'pending',
      request_date: record.request_date || '',
      required_date: record.required_date || '',
      supplier_suggestion: record.supplier_suggestion || '',
      notes: record.notes || '',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/demandes-achat/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadRequests();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
      }
    }
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/demandes-achat/${recordId}`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      loadRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/achats/demandes-achat/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedRecords([]);
        loadRequests();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      request_number: '',
      requester_name: '',
      requester_department: '',
      product_id: '',
      product_name: '',
      product_code: '',
      quantity: '',
      unit_price: '',
      total_amount: '',
      urgency: 'normal',
      status: 'pending',
      request_date: new Date().toISOString().split('T')[0],
      required_date: '',
      supplier_suggestion: '',
      notes: '',
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'request_number', label: p('col_request_number') },
      { key: 'requester_name', label: p('col_requester') },
      { key: 'product_name', label: p('col_product') },
      { key: 'quantity', label: p('col_quantity') },
      { key: 'total_amount', label: p('col_amount') },
      { key: 'urgency', label: p('col_urgency') },
      { key: 'status', label: p('col_status') },
      { key: 'request_date', label: p('col_request_date') },
    ];

    const fileName = `demandes_achat_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredRequests, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const filteredData = filteredRequests;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_request_number')}</th>
                <th>${p('col_supplier')}</th>
                <th>${p('label_date').replace(':', '')}</th>
                <th>${p('col_amount')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.request_number || 'N/A'}</td>
                  <td>${item.supplier_name || 'N/A'}</td>
                  <td>${fmtDate(item.request_date)}</td>
                  <td>${formatBrandMoney(item.total_amount || 0)}</td>
                  <td>${item.status || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    const headers = [
      { key: 'request_number', label: p('col_request_number') },
      { key: 'requester_name', label: p('col_requester') },
      { key: 'product_name', label: p('col_product') },
      { key: 'quantity', label: p('col_quantity') },
      { key: 'total_amount', label: p('col_amount') },
      { key: 'urgency', label: p('col_urgency') },
      { key: 'status', label: p('col_status') },
      { key: 'request_date', label: p('col_request_date') },
    ];
    const fileName = `demandes_achat_${new Date().toISOString().split('T')[0]}.xlsx`;
    try {
      setIsGeneratingExcel(true);
      await exportToExcel(filteredRequests, headers, fileName, p('title'));
    } catch (error) {
      console.error('Excel generation error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'request_number', label: p('col_request_number') },
      { key: 'supplier_name', label: p('col_supplier') },
      { key: 'request_date', label: p('col_request_date') },
      { key: 'total_amount', label: p('col_amount') }
    ];

    const schema = {
      requiredFields: ['supplier_name', 'request_date'],
      validators: {
        total_amount: (value) => !value || !isNaN(parseFloat(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/demandes', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadRequests();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRequests.map((r) => r.id));
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };


  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total_returns'), value: stats.total, icon: FileText, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-amber-500 to-amber-600', variant: 'forest' },
      { key: 'approved', label: p('stat_approved'), value: stats.approved, icon: CheckCircle, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'converted', label: p('dist_converted'), value: stats.converted, icon: Package, color: 'from-green-500 to-green-600', variant: 'forest' },
    ],
    [stats, p],
  );

  const demandesColumnKeys = useMemo(
    () => ['request_number', 'requester_name', 'request_date', 'product_name', 'total_amount', 'urgency', 'status', 'required_date', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(demandesColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'request_number', labelKey: 'col_request_number' },
      { key: 'requester_name', labelKey: 'col_requester' },
      { key: 'request_date', labelKey: 'col_request_date' },
      { key: 'product_name', labelKey: 'col_product' },
      { key: 'total_amount', labelKey: 'col_amount' },
      { key: 'urgency', labelKey: 'col_urgency' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'required_date', labelKey: 'col_deadline' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const advancedOptionsContent = (
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_requests' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enablePurchaseRequests ? p('notice_requests_disabled', { defaultValue: 'طلبات الشراء معطّلة — فعّلها من إعدادات المشتريات ← طلبات وأوامر الشراء.' }) : null}
      />
      <AchatsConfigNotice
        message={achatsConfig.lowStockAlertEnabled && lowStockCatalogCount > 0
          ? p('notice_low_stock_catalog', {
              defaultValue: '{{count}} منتج في فهرس الموردين تحت حد إعادة الطلب — راجع طلبات الشراء.',
              count: lowStockCatalogCount,
            })
          : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={FileText}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedRecords.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedRecords.length})`, title: p('bulk_delete_confirm', { count: selectedRecords.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={() => {
          if (!achatsConfig.enablePurchaseRequests) {
            alert(p('feature_disabled', { defaultValue: 'طلبات الشراء معطّلة من إعدادات المشتريات.' }));
            return;
          }
          setEditingRecord(null);
          setFormData({
            request_number: generateRequestNumber(),
            requester_name: '',
            requester_department: '',
            product_id: '',
            product_name: '',
            product_code: '',
            quantity: '',
            unit_price: '',
            total_amount: '',
            urgency: 'normal',
            status: getDefaultApprovalStatus(achatsSettings, 'purchaseRequest'),
            request_date: new Date().toISOString().split('T')[0],
            required_date: '',
            supplier_suggestion: '',
            notes: '',
          });
          setFormTab('general');
          setShowModal(true);
        }}
        createLabel={p('btn_create')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={loadRequests} className="refresh-button" title={t('common.refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
                  tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColumnVisible('request_number') ? <TableColumnHeaderFilter {...colHeader('request_number', p('col_request_number'))} /> : null}
                  {isColumnVisible('requester_name') ? <TableColumnHeaderFilter {...colHeader('requester_name', p('col_requester'))} /> : null}
                  {isColumnVisible('request_date') ? <TableColumnHeaderFilter {...colHeader('request_date', p('col_request_date'))} /> : null}
                  {isColumnVisible('product_name') ? <TableColumnHeaderFilter {...colHeader('product_name', p('col_product_qty'))} /> : null}
                  {isColumnVisible('total_amount') ? <TableColumnHeaderFilter {...colHeader('total_amount', p('col_amount'))} /> : null}
                  {isColumnVisible('urgency') ? <TableColumnHeaderFilter {...colHeader('urgency', p('col_urgency'), { kind: 'select', options: urgencyOptions })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  {isColumnVisible('required_date') ? <TableColumnHeaderFilter {...colHeader('required_date', p('col_deadline'))} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedRequests.length > 0 ? (
                  displayedRequests.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      
                      {isColumnVisible('request_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.request_number}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('created_label')} {record.created_at ? fmtDate(record.created_at) : '-'}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('requester_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.requester_name || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.requester_department || '—'}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('request_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {record.request_date ? fmtDate(record.request_date) : '-'}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">{record.request_date ? p('days_ago', { days: Math.floor((Date.now() - new Date(record.request_date).getTime()) / (1000 * 60 * 60 * 24)) }) : ''}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('product_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.product_name || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('label_qty_prefix')} {record.quantity ?? '-'} {record.product_code ? `| ${record.product_code}` : ''}</span>
                          {achatsConfig.lowStockAlertEnabled && isAchatsCatalogLowStock(getRequestCatalogItem(record)) ? (
                            <span className="text-amber-400 text-xs mt-0.5">{p('badge_low_stock', { defaultValue: 'مخزون منخفض في فهرس المورد' })}</span>
                          ) : null}
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('total_amount') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatBrandMoney(record.total_amount || 0)}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.unit_price ? `${formatBrandMoney(record.unit_price)}/${p('label_qty_prefix').replace(':', '').trim() || 'و'}` : ''}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('urgency') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{getUrgencyLabel(record.urgency)}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.supplier_suggestion || ''}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                      ) : null}
                      
                      {isColumnVisible('required_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.required_date ? fmtDate(record.required_date) : '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('label_required_date')}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view')}
                          editTitle={p('action_edit')}
                          deleteTitle={p('action_delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_state')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedRequests.length > 0 ? (
              displayedRequests.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><FileText size={20} className="text-emerald-400" />{record.request_number}</h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                        {record.request_date && (<span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{fmtDate(record.request_date)}</span>)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_return_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_request_number')}</span> {record.request_number}</div>
                            <div><span className="font-medium">{p('label_date')}</span> {record.request_date ? fmtDate(record.request_date) : '-'}</div>
                            <div><span className="font-medium">{p('label_amount')}</span> {formatBrandMoney(record.total_amount || 0)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_supplier')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_requester_colon')}</span> {record.requester_name || '-'}</div>
                            <div><span className="font-medium">{p('label_product_colon')}</span> {record.product_name || '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_reason_refund')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('field_urgency')}</span> {getUrgencyLabel(record.urgency)}</div>
                            <div><span className="font-medium">{p('field_requester_service')}</span> {record.requester_department || '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_origin_order')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_product_colon')}</span> {record.product_name || '-'}</div>
                            <div><span className="font-medium">{p('label_qty_prefix')}</span> {record.quantity ?? '-'}</div>
                            {record.notes && (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <span className="font-medium text-white/80 text-xs">{p('label_notes')}</span>
                                <p className="text-white/60 text-xs mt-1">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_state')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => {
          setShowViewModal(false);
          setViewingRecord(null);
        }}
        title={p('view_modal_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}><FileText size={18} className="text-emerald-400" />{p('section_return_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={m.viewLabel}>{p('label_request_number_field')}</p><p className={m.viewValueLg}>{viewingRecord.request_number}</p></div>
                    <div><p className={m.viewLabel}>{p('col_request_date')}</p><p className={m.viewValue}>{viewingRecord.request_date ? fmtDate(viewingRecord.request_date) : '-'}</p></div>
                    <div><p className={m.viewLabel}>{p('col_amount')}</p><p className={m.viewValueLg}>{formatBrandMoney(viewingRecord.total_amount || 0)}</p></div>
                    <div><p className={m.viewLabel}>{p('col_status')}</p><div className="flex">{getStatusBadge(viewingRecord.status)}</div></div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}><Building2 size={18} className="text-blue-400" />{p('section_supplier')}</h3>
                    <div className="space-y-3">
                      <div><p className={m.viewLabel}>{p('label_name').replace(':', '')}</p><p className={m.viewValue}>{viewingRecord.supplier_name}</p></div>
                      <div><p className={m.viewLabel}>{p('label_supplier_id')}</p><p className={m.viewValue}>{viewingRecord.supplier_id || 'N/A'}</p></div>
                    </div>
                  </div>

                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}><FileText size={18} className="text-yellow-400" />{p('section_reason_refund')}</h3>
                    <div className="space-y-3">
                      <div><p className={m.viewLabel}>{p('field_urgency')}</p><p className={m.viewValue}>{getUrgencyLabel(viewingRecord.urgency)}</p></div>
                      <div><p className={m.viewLabel}>{p('field_requester_service')}</p><p className={m.viewValue}>{viewingRecord.requester_department || '-'}</p></div>
                      {viewingRecord.notes && (<div><p className={m.viewLabel}>{p('label_notes').replace(':', '')}</p><p className="text-white/80 text-sm bg-white/5 p-3 rounded-lg">{viewingRecord.notes}</p></div>)}
                    </div>
                  </div>
                </div>
        </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
        icon={FileText}
        formId="request-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="request-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                <div className={ACHATS_FORM_GRID}>
                          <div>
                            <label className={cfgTheme.label}>{p('label_request_number_field')}</label>
                            <input type="text" value={formData.request_number} onChange={(e) => setFormData({ ...formData, request_number: e.target.value })} className={cfgTheme.input} placeholder={p('placeholder_auto_generated')} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_request_date_required')} /></label>
                            <input type="date" required value={formData.request_date} onChange={(e) => setFormData({ ...formData, request_date: e.target.value })} className={cfgTheme.input} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_required_date')}</label>
                            <input type="date" value={formData.required_date} onChange={(e) => setFormData({ ...formData, required_date: e.target.value })} className={cfgTheme.input} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_status_required')} /></label>
                            <div className="relative" ref={statusDropdownRef}>
                              <button type="button" data-status-dropdown-button="true" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between">
                                <span>{statusOptions.find(s => s.value === formData.status)?.label || p('select')}</span>
                                <ChevronDown size={16} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                              </button>
                              {showStatusDropdown && ReactDOM.createPortal(
                                <div data-status-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(statusDropdownRef.current && (() => {
                                  const rect = statusDropdownRef.current.getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const spaceAbove = rect.top;
                                  const dropdownHeight = 180;
                                  return spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width } : { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                                  {statusOptions.map(option => (
                                    <button key={option.value} type="button" onClick={() => { setFormData({ ...formData, status: option.value }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">
                                      <option.icon size={16} className="mr-2" />{option.label}
                                    </button>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </div>
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('col_requester')} required /></label>
                            <input type="text" required value={formData.requester_name} onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })} className={cfgTheme.input} placeholder={p('col_requester')} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_requester_dept')}</label>
                            <input type="text" value={formData.requester_department} onChange={(e) => setFormData({ ...formData, requester_department: e.target.value })} className={cfgTheme.input} placeholder={p('placeholder_department')} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('col_product')} required /></label>
                            {loadingProducts ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('select')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.product_id}
                                onChange={(value) => handleProductSelection(value)}
                                options={productOptions}
                                placeholder={p('col_product')}
                                searchable
                                required
                              />
                            )}
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{t('modules.achats.pages.catalogueFournisseurPage.col_product_code', { defaultValue: 'رمز المنتج' })}</label>
                            <input type="text" value={formData.product_code} onChange={(e) => setFormData({ ...formData, product_code: e.target.value })} className={cfgTheme.input} />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('col_quantity')} required /></label>
                            <input type="number" min="1" required value={formData.quantity} onChange={(e) => {
                              const quantity = e.target.value;
                              const unitPrice = parseFloat(formData.unit_price) || 0;
                              setFormData({
                                ...formData,
                                quantity,
                                total_amount: quantity && unitPrice ? (parseFloat(quantity) * unitPrice).toFixed(2) : formData.total_amount,
                              });
                            }} className={cfgTheme.input} placeholder="1" />
                          </div>
                        </div>
              )}

                {formTab === 'details' && (
                <div className={ACHATS_FORM_GRID}>
                          <div>
                            <label className={cfgTheme.label}>{t('modules.achats.pages.commandesFournisseursPage.col_unit_price_short', { defaultValue: 'سعر الوحدة' })}</label>
                            <input type="number" step="0.01" value={formData.unit_price} onChange={(e) => {
                              const unitPrice = e.target.value;
                              const qty = parseFloat(formData.quantity) || 0;
                              setFormData({
                                ...formData,
                                unit_price: unitPrice,
                                total_amount: qty && unitPrice ? (qty * parseFloat(unitPrice)).toFixed(2) : formData.total_amount,
                              });
                            }} className={cfgTheme.input} placeholder="0.00" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('label_total_amount')}</label>
                            <input type="number" step="0.01" value={formData.total_amount} onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })} className={cfgTheme.input} placeholder="0.00" />
                          </div>
                          <div>
                            <label className={cfgTheme.label}><RequiredLabelText text={p('label_urgency_required')} /></label>
                            <div className="relative" ref={reasonDropdownRef}>
                              <button type="button" data-reason-dropdown-button="true" onClick={() => setShowReasonDropdown(!showReasonDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between">
                                <span>{getUrgencyLabel(formData.urgency)}</span>
                                <ChevronDown size={16} className={`transition-transform ${showReasonDropdown ? 'rotate-180' : ''}`} />
                              </button>
                              {showReasonDropdown && ReactDOM.createPortal(
                                <div data-reason-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(reasonDropdownRef.current && (() => {
                                  const rect = reasonDropdownRef.current.getBoundingClientRect();
                                  return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                                  {urgencyOptions.map(option => (
                                    <button key={option.value} type="button" onClick={() => { setFormData({ ...formData, urgency: option.value }); setShowReasonDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{option.label}</button>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </div>
                          </div>
                          <div>
                            <label className={cfgTheme.label}>{p('col_supplier')}</label>
                            <input type="text" value={formData.supplier_suggestion} onChange={(e) => setFormData({ ...formData, supplier_suggestion: e.target.value })} className={cfgTheme.input} placeholder={p('placeholder_supplier_name')} />
                          </div>
                          <div className="md:col-span-3">
                            <label className={cfgTheme.label}>{p('label_notes').replace(':', '')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" rows="4" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
              )}

        </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default DemandesAchatPage;




